import React, { FC, memo, KeyboardEvent } from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatFraudCategory } from "../../utils/formatFraudCategory";

interface FraudCategoryCardProps {
  category: string;
  riskScore?: number;
  riskLevel?: string;
  isSelected: boolean;
  onClick: () => void;
  /**
   * Overrides the "RISK SCORE" caption. The ID Documents card carries the
   * provider's own workflow score, which is not on the fraud modules' risk scale —
   * Socure has not confirmed theirs — so calling it a risk score would be a claim
   * we cannot support.
   */
  scoreLabel?: string;
  /**
   * Overrides the risk-level colour. Document verification has no risk level; its
   * colour comes from the decision instead.
   */
  scoreColorOverride?: string;
  /**
   * What to say in place of a number the card does not have — "Not requested",
   * "Pending". Rendered on the value's line, so the card keeps the same three
   * lines and the same height as every other card in the strip.
   *
   * Only for the no-number case. Text beside a number widened the card past its
   * neighbours and then clipped, which is why anything that has to coexist with a
   * score goes through `noteTooltip` instead.
   */
  note?: string;
  /**
   * Something true about the number that the number cannot say — most of all
   * that a newer verification is outstanding, so a green "accept" is a report of
   * the past rather than a settled present.
   *
   * An icon and a tooltip rather than a caption: a card in a fixed row has no
   * width to spare for a sentence, and this one is worth a sentence.
   */
  noteTooltip?: string;
  /**
   * A word in place of the score — "Clear", "Match" — for a card whose result is
   * an outcome rather than a number (the OFAC card). Rendered exactly where and
   * how a score is, in `scoreColorOverride`, so it reads as the card's value and
   * not as a caveat the way an orange `note` does.
   */
  value?: string;
}

/**
 * Risk level to colour. Exported so a card whose value is not a risk score can
 * still borrow the strip's palette rather than restating these hexes — see the
 * ID Documents card, which maps a decision onto these bands.
 */
export const getRiskScoreColor = (riskLevel?: string): string => {
  switch (riskLevel) {
    case "high":
      return "#eb2835"; // Red
    case "medium":
      return "#e2832c"; // Orange
    default:
      return "#41be1f"; // Green for low/none
  }
};

/** The colour a card already uses for a value it does not have. */
export const NEUTRAL_SCORE_COLOR = "#a2b0bc";

const FraudCategoryCardComponent: FC<FraudCategoryCardProps> = ({
  category,
  riskScore,
  riskLevel,
  isSelected,
  onClick,
  scoreLabel = "risk score",
  scoreColorOverride,
  note,
  noteTooltip,
  value,
}) => {
  const scoreColor = scoreColorOverride ?? getRiskScoreColor(riskLevel);
  const hasScore = riskScore !== undefined && riskScore !== null;
  // Divided by 100 and shown to three decimals, so the strip reads as one column
  // of comparable numbers.
  //
  // Sound for the fraud cards: those are 0-1 module scores that
  // SocureFraudTransformService multiplies by 100 on the way in, so dividing here
  // returns them to their original scale. The ID Documents card rides the same
  // treatment on the ASSUMPTION its provider score is also 0-100 — observed
  // sandbox values (48, 30.5, 20.7) are consistent with that, but Socure has not
  // confirmed the scale and sandbox has returned negatives, so this is not a
  // documented fact. If the workflow score turns out to be 0-1, this card reads
  // 100x low beside the others and the fix belongs at the boundary that persists
  // it, not here.
  const displayScore =
    value ?? (hasScore ? (Number(riskScore) / 100).toFixed(3) : null);
  const formattedCategory = formatFraudCategory(category);
  const baseAriaLabel =
    displayScore !== null
      ? `${formattedCategory} fraud category, ${scoreLabel} ${displayScore}`
      : `${formattedCategory} fraud category, no ${scoreLabel}`;
  // Both are part of the card's meaning, not decoration, so both are announced —
  // a tooltip that only exists on hover is invisible to a screen reader.
  const ariaLabel = [baseAriaLabel, note, noteTooltip]
    .filter(Boolean)
    .join(", ");

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={ariaLabel}
      className="text-dark"
      style={{
        minWidth: "110px",
        padding: "10px 12px",
        border: isSelected ? "2px solid #00b4a1" : "1px solid #d0d0d0",
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#ffffff" : "#f8f9fa",
        transition: "border-color 0.2s ease",
      }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div style={{ fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>
        {formattedCategory}
      </div>
      <div
        className="text-uppercase"
        style={{ fontSize: "7pt", color: "#a2b0bc" }}
      >
        {scoreLabel}
      </div>
      {/*
        One line for what the card reports, always in the same place.
        A note sits ON this line — alone where there is no number, beside it
        where there is — rather than sometimes displacing the caption above and
        sometimes appearing below it, which read as two different card layouts.
        The fixed height keeps a card carrying only a note aligned with the
        numbers either side of it; `1.25rem × 1.4` is the score's own line box.
      */}
      <div
        style={{
          minHeight: "1.75rem",
          display: "flex",
          alignItems: "baseline",
          gap: "6px",
        }}
      >
        {displayScore !== null && (
          <span
            style={{
              // A word longer than a score ("List match") steps down a size so
              // it fits a card at its 110px minimum.
              fontSize:
                value !== undefined && value.length > 7 ? "1rem" : "1.25rem",
              fontWeight: 600,
              color: scoreColor,
            }}
          >
            {displayScore}
          </span>
        )}
        {noteTooltip && (
          <Tooltip title={noteTooltip}>
            <InfoCircleOutlined
              style={{ color: "#e2832c", fontSize: "0.8rem" }}
            />
          </Tooltip>
        )}
        {note ? (
          <span
            className="text-uppercase"
            style={{ fontSize: "7pt", fontWeight: 600, color: "#e2832c" }}
          >
            {note}
          </span>
        ) : (
          // No number and nothing to say about why: the em dash. Never both —
          // "—" beside "NOT REQUESTED" says one thing twice, and the dash is
          // the half that says nothing.
          displayScore === null && (
            <span
              style={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#a2b0bc",
              }}
            >
              —
            </span>
          )
        )}
      </div>
    </div>
  );
};

export const FraudCategoryCard = memo(FraudCategoryCardComponent);

export default FraudCategoryCard;
